import React, { useEffect, useMemo, useRef, useState } from "react";
import axios, { AxiosError } from "axios";
import { baseUrl } from "../../constants";
import { useNavigate, useParams } from "react-router-dom";
import {
  alpha,
  Box,
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Divider,
  Badge,
  Paper,
  Stack,
  Chip,
  Avatar,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import FolderIcon from "@mui/icons-material/Folder";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
import PushPinIcon from "@mui/icons-material/PushPin";
import dayjs from "dayjs";
import { useChatSocket } from "./ChatSocketProvider";

/* ----------------------------- Types ----------------------------- */
type ChannelScope = "WORKSPACE" | "DEPARTMENT" | "PROJECT" | "ACCOUNT";

type Channel = {
  id: number;
  name: string;
  scope: ChannelScope;
  targetId: number | null;
  createdAt: string;
  updatedAt: string;
};

type Author = {
  id: number;
  username: string;
  profile?: { name?: string | null } | null;
};
type MessageHit = {
  id: number;
  channelId: number;
  content: string;
  createdAt: string;
  author: Author;
  snippet?: string;
};

const SCOPES: ChannelScope[] = [
  "WORKSPACE",
  "DEPARTMENT",
  "PROJECT",
  "ACCOUNT",
];
type FilterKey = "ALL" | "PINNED" | ChannelScope;

/* ----------------------------- Helpers ----------------------------- */

const tokenHeader = () => {
  const token =
    localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

function highlight(text: string, term: string) {
  if (!term) return <>{text}</>;
  const iText = text || "";
  const iTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${iTerm})`, "ig");
  const parts = iText.split(re);
  return (
    <>
      {parts.map((p, i) =>
        re.test(p) ? (
          <Box
            key={i}
            component="mark"
            sx={{
              p: 0,
              m: 0,
              background: "transparent",
              color: "inherit",
              fontWeight: 700,
            }}
          >
            {p}
          </Box>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

function niceScopeLabel(scope: ChannelScope) {
  switch (scope) {
    case "WORKSPACE":
      return "Workspace";
    case "DEPARTMENT":
      return "Departments";
    case "PROJECT":
      return "Projects";
    case "ACCOUNT":
      return "Accounts";
  }
}

function truncate(s: string, n = 120) {
  if (!s) return s;
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

// normalize various backend shapes to grouped hits
function groupMessageHits(data: any): Record<number, MessageHit[]> {
  if (
    data?.grouped &&
    typeof data.grouped === "object" &&
    !Array.isArray(data.grouped)
  ) {
    return data.grouped as Record<number, MessageHit[]>;
  }
  if (Array.isArray(data?.results)) {
    const out: Record<number, MessageHit[]> = {};
    for (const r of data.results) {
      const cid = Number(r.channelId);
      (out[cid] ||= []).push(...(r.messages || []));
    }
    return out;
  }
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const entries = Object.entries(data);
    if (entries.length && entries.every(([, v]) => Array.isArray(v))) {
      const out: Record<number, MessageHit[]> = {};
      for (const [k, v] of entries) out[Number(k)] = v as MessageHit[];
      return out;
    }
  }
  let flat: MessageHit[] = [];
  if (Array.isArray(data)) flat = data as MessageHit[];
  else if (Array.isArray(data?.messages)) flat = data.messages as MessageHit[];

  const out: Record<number, MessageHit[]> = {};
  for (const m of flat) {
    const cid = Number((m as any).channelId);
    (out[cid] ||= []).push(m);
  }
  return out;
}

async function searchMessagesAPI(q: string, signal?: AbortSignal) {
  const headers = tokenHeader();
  if (!q?.trim()) return {};
  try {
    const { data } = await axios.get(`${baseUrl}/chat/messages/search`, {
      params: { q, limit: 100 },
      headers,
      signal,
    });
    return data;
  } catch (err) {
    const e = err as AxiosError;
    if (e.response?.status === 404) return { __unsupported: true };
    throw err;
  }
}

// local pin/favorites (no backend)
const PINS_KEY = "chat:pins";
function loadPins(): number[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}
function savePins(ids: number[]) {
  localStorage.setItem(PINS_KEY, JSON.stringify(ids));
}

function getScopeColor(scope: ChannelScope, theme: any) {
  switch (scope) {
    case "WORKSPACE":
      return theme.palette.primary.main;
    case "DEPARTMENT":
      return theme.palette.info.main;
    case "PROJECT":
      return theme.palette.success.main;
    case "ACCOUNT":
      return theme.palette.warning.main;
  }
}
function getScopeIcon(scope: ChannelScope): JSX.Element {
  switch (scope) {
    case "WORKSPACE":
      return <WorkspacesIcon fontSize="small" />;
    case "DEPARTMENT":
      return <CorporateFareIcon fontSize="small" />;
    case "PROJECT":
      return <FolderIcon fontSize="small" />;
    case "ACCOUNT":
      return <AccountCircleIcon fontSize="small" />;
  }
}

/* ---------------------------- Component ---------------------------- */

export default function ChannelList() {
  const theme = useTheme();
  const [channels, setChannels] = useState<Channel[] | null>(null);
  const [loading, setLoading] = useState(false);

  // unread by channel id
  const [unread, setUnread] = useState<Record<number, number>>({});

  // search states
  const [query, setQuery] = useState("");
  const [msgHits, setMsgHits] = useState<Record<number, MessageHit[]>>({});
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgSupported, setMsgSupported] = useState(true);

  // open/closed per group
  const [open, setOpen] = useState<Record<ChannelScope, boolean>>({
    WORKSPACE: true,
    DEPARTMENT: true,
    PROJECT: true,
    ACCOUNT: true,
  });

  // pins
  const [pinned, setPinned] = useState<number[]>(() => loadPins());

  // NEW: modern filter chips
  const [activeFilter, setActiveFilter] = useState<FilterKey>("ALL");
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);

  const { channelId } = useParams();
  const selectedId = Number(channelId);
  const navigate = useNavigate();
  const { socket } = useChatSocket();

  // lookup cache
  const channelsByIdRef = useRef<Map<number, Channel>>(new Map());

  const fetchUnreadSnapshot = async () => {
    try {
      const { data } = await axios.get<Record<number, number>>(
        `${baseUrl}/chat/unread-counts`,
        { headers: tokenHeader() }
      );
      const current = Number(channelId);
      const next: Record<number, number> = {};
      for (const [cid, cnt] of Object.entries(data || {})) {
        const id = Number(cid);
        next[id] = id === current ? 0 : Number(cnt || 0);
      }
      setUnread(next);
    } catch {
      // ignore; socket events may fill in
    }
  };

  useEffect(() => {
    fetchUnreadSnapshot();
  }, []); // once

  /* --------------------- channels (HTTP) ---------------------- */
  useEffect(() => {
    setLoading(true);
    axios
      .get<Channel[]>(`${baseUrl}/chat/channels`, { headers: tokenHeader() })
      .then((res) => {
        const list = res.data || [];
        setChannels(list);
        const map = new Map<number, Channel>();
        list.forEach((c) => map.set(c.id, c));
        channelsByIdRef.current = map;
      })
      .catch(() => setChannels([]))
      .finally(() => setLoading(false));
  }, []);

  /* -------------------- unread via socket --------------------- */
  useEffect(() => {
    if (!socket) return;

    const onConnect = () => {
      socket.emit("unread.request");
    };

    const onUnread = (p: { channelId: number; unread: number }) => {
      const current = Number(channelId);
      setUnread((prev) => ({
        ...prev,
        [p.channelId]: p.channelId === current ? 0 : p.unread,
      }));
    };

    const onUnreadSync = (map: Record<number, number>) => {
      const current = Number(channelId);
      const next: Record<number, number> = {};
      for (const [cid, cnt] of Object.entries(map || {})) {
        const id = Number(cid);
        next[id] = id === current ? 0 : Number(cnt || 0);
      }
      setUnread(next);
    };

    socket.on("connect", onConnect);
    socket.on("channel.unread", onUnread);
    socket.on("unread.sync", onUnreadSync);

    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("channel.unread", onUnread);
      socket.off("unread.sync", onUnreadSync);
    };
  }, [socket, channelId]);

  // clear unread for current channel when selection changes
  useEffect(() => {
    if (!selectedId) return;
    setUnread((prev) =>
      prev[selectedId] ? { ...prev, [selectedId]: 0 } : prev
    );
  }, [selectedId]);

  /* ---------------- filter/group for classic ------------------ */
  const qLower = query.trim().toLowerCase();

  const allChannels = useMemo(() => channels ?? [], [channels]);

  // Counts for chips (respect search text but ignore active filter)
  const scopeCounts = useMemo(() => {
    const base = allChannels.filter((c) =>
      qLower ? c.name.toLowerCase().includes(qLower) : true
    );
    return {
      ALL: base.length,
      PINNED: base.filter((c) => pinned.includes(c.id)).length,
      WORKSPACE: base.filter((c) => c.scope === "WORKSPACE").length,
      DEPARTMENT: base.filter((c) => c.scope === "DEPARTMENT").length,
      PROJECT: base.filter((c) => c.scope === "PROJECT").length,
      ACCOUNT: base.filter((c) => c.scope === "ACCOUNT").length,
    };
  }, [allChannels, qLower, pinned]);

  // Apply chip filter + unreadOnly on top of text filter
  const channelPassesFilters = (c: Channel) => {
    if (qLower && !c.name.toLowerCase().includes(qLower)) return false;

    if (activeFilter === "PINNED" && !pinned.includes(c.id)) return false;
    if (
      activeFilter !== "ALL" &&
      activeFilter !== "PINNED" &&
      c.scope !== activeFilter
    )
      return false;

    if (unreadOnly && (unread[c.id] || 0) === 0) return false;

    return true;
  };

  const pinnedChannels = useMemo(
    () =>
      allChannels
        .filter((c) => pinned.includes(c.id))
        .filter(channelPassesFilters)
        .sort((a, b) => a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allChannels, pinned, qLower, activeFilter, unreadOnly, unread]
  );

  const grouped = useMemo(() => {
    const list = allChannels
      .filter((c) => !pinned.includes(c.id)) // exclude pinned from groups
      .filter(channelPassesFilters);

    const map: Record<ChannelScope, Channel[]> = {
      WORKSPACE: [],
      DEPARTMENT: [],
      PROJECT: [],
      ACCOUNT: [],
    };
    list.forEach((c) => map[c.scope].push(c));
    SCOPES.forEach((s) => map[s].sort((a, b) => a.name.localeCompare(b.name)));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChannels, pinned, qLower, activeFilter, unreadOnly, unread]);

  /* ------------------- debounced msg search ------------------- */
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setMsgHits({});
      setMsgLoading(false);
      setMsgSupported(true);
      return;
    }
    const controller = new AbortController();
    setMsgLoading(true);
    const t = window.setTimeout(() => {
      searchMessagesAPI(q, controller.signal)
        .then((data) => {
          if (data?.__unsupported) {
            setMsgSupported(false);
            setMsgHits({});
          } else {
            setMsgSupported(true);
            const grouped = groupMessageHits(data);
            setMsgHits(grouped);
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          setMsgSupported(false);
          setMsgHits({});
        })
        .finally(() => setMsgLoading(false));
    }, 220);

    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [query]);

  /* ------------------------ handlers -------------------------- */
  const handleOpenChannel = (id: number) => {
    setUnread((prev) => (prev[id] ? { ...prev, [id]: 0 } : prev)); // optimistic clear
    navigate(`/chat/${id}`);
  };

  const handleOpenMessageHit = (hit: MessageHit) => {
    sessionStorage.setItem(
      "chat:jump",
      JSON.stringify({ channelId: hit.channelId, messageId: hit.id })
    );
    navigate(`/chat/${hit.channelId}?mid=${hit.id}`);
  };

  const togglePin = (id: number) => {
    setPinned((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      const arr = Array.from(set);
      savePins(arr);
      return arr;
    });
  };

  const setFilter = (key: FilterKey) => setActiveFilter(key);
  const toggleUnreadOnly = () => setUnreadOnly((v) => !v);

  /* ---------------- derived: search mode ------------------- */
  const isSearching = query.trim().length > 0;

  const chatMatches = useMemo(() => {
    if (!isSearching) return [];
    return allChannels
      .filter(channelPassesFilters)
      .sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, allChannels, qLower, activeFilter, unreadOnly, unread]);

  /* --------------------------- UI ----------------------------- */
  if (loading || channels === null) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={38} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={180} />
      </Box>
    );
  }

  if (!channels.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No channels available.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        bgcolor:
          theme.palette.mode === "light"
            ? theme.palette.background.paper
            : "transparent",
      }}
    >
      {/* Search + chips (sticky) */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          backdropFilter: "blur(6px)",
        }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            fullWidth
            placeholder="Search chats or messages…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <Tooltip title="Clear">
                    <IconButton
                      size="small"
                      onClick={() => setQuery("")}
                      aria-label="Clear search"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {/* Filter chips */}
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <FilterChip
            label={`All (${scopeCounts.ALL})`}
            selected={activeFilter === "ALL"}
            onClick={() => setFilter("ALL")}
          />
          <FilterChip
            label={`Pinned (${scopeCounts.PINNED})`}
            icon={<PushPinIcon fontSize="small" />}
            selected={activeFilter === "PINNED"}
            onClick={() => setFilter("PINNED")}
          />
          <ScopeFilterChip
            scope="WORKSPACE"
            count={scopeCounts.WORKSPACE}
            selected={activeFilter === "WORKSPACE"}
            onClick={() => setFilter("WORKSPACE")}
          />
          <ScopeFilterChip
            scope="DEPARTMENT"
            count={scopeCounts.DEPARTMENT}
            selected={activeFilter === "DEPARTMENT"}
            onClick={() => setFilter("DEPARTMENT")}
          />
          <ScopeFilterChip
            scope="PROJECT"
            count={scopeCounts.PROJECT}
            selected={activeFilter === "PROJECT"}
            onClick={() => setFilter("PROJECT")}
          />
          <ScopeFilterChip
            scope="ACCOUNT"
            count={scopeCounts.ACCOUNT}
            selected={activeFilter === "ACCOUNT"}
            onClick={() => setFilter("ACCOUNT")}
          />
          <Chip
            size="small"
            variant={unreadOnly ? "filled" : "outlined"}
            color={unreadOnly ? "secondary" : "default"}
            icon={<MarkUnreadChatAltIcon fontSize="small" />}
            label="Unread only"
            onClick={toggleUnreadOnly}
            sx={{ ml: { xs: 0, md: "auto" } }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {!isSearching ? (
          <Stack spacing={1.25} sx={{ p: 1.25 }}>
            {/* ---------- Pinned (only if using All or Pinned) ---------- */}
            {(activeFilter === "ALL" || activeFilter === "PINNED") && (
              <ScopeCard
                title="Pinned"
                count={pinnedChannels.length}
                collapsible
                defaultOpen
                dense
              >
                {pinnedChannels.length ? (
                  <List dense disablePadding>
                    {pinnedChannels.map((c, idx) => (
                      <React.Fragment key={`p-${c.id}`}>
                        <ChannelRow
                          channel={c}
                          selected={selectedId === c.id}
                          unread={unread[c.id] || 0}
                          onOpen={() => handleOpenChannel(c.id)}
                          onTogglePin={() => togglePin(c.id)}
                          pinned
                        />
                        {idx < pinnedChannels.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <MutedHint text="No pinned chats yet. Click the star to pin a channel." />
                )}
              </ScopeCard>
            )}

            {/* ---------- Grouped by scope (respects filter) ---------- */}
            {SCOPES.filter(
              (s) => activeFilter === "ALL" || activeFilter === s
            ).map((scope) => {
              const list = grouped[scope];
              const count = list.length;
              return (
                <ScopeCard
                  key={scope}
                  title={niceScopeLabel(scope)}
                  count={count}
                  scope={scope}
                  collapsible
                  open={open[scope]}
                  onToggle={() =>
                    setOpen((p) => ({ ...p, [scope]: !p[scope] }))
                  }
                >
                  {count ? (
                    <List dense disablePadding>
                      {list.map((c, idx) => (
                        <React.Fragment key={c.id}>
                          <ChannelRow
                            channel={c}
                            selected={selectedId === c.id}
                            unread={unread[c.id] || 0}
                            onOpen={() => handleOpenChannel(c.id)}
                            onTogglePin={() => togglePin(c.id)}
                            pinned={pinned.includes(c.id)}
                          />
                          {idx < list.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <MutedHint
                      text={`No ${niceScopeLabel(
                        scope
                      ).toLowerCase()} channels`}
                    />
                  )}
                </ScopeCard>
              );
            })}
          </Stack>
        ) : (
          // -------- search results (two sections) --------
          <Stack spacing={2} sx={{ p: 1.25 }}>
            {/* CHATS */}
            <ResultCard
              title="Chats"
              icon={<ChatBubbleOutlineIcon fontSize="small" />}
              count={chatMatches.length}
            >
              {chatMatches.length === 0 ? (
                <MutedHint text={`No chats match “${query}”.`} />
              ) : (
                <List dense disablePadding>
                  {chatMatches.map((c, idx) => {
                    const unreadCount = unread[c.id] || 0;
                    return (
                      <React.Fragment key={`c-${c.id}`}>
                        <ListItemButton onClick={() => handleOpenChannel(c.id)}>
                          <ScopeAvatar channel={c} />
                          <ListItemText
                            primary={highlight(c.name, query)}
                            secondary={niceScopeLabel(c.scope)}
                            secondaryTypographyProps={{
                              sx: { opacity: 0.7 },
                            }}
                            sx={{ ml: 1 }}
                          />
                          <Badge
                            color="primary"
                            badgeContent={unreadCount}
                            max={99}
                            invisible={unreadCount === 0}
                            sx={{ mr: 1 }}
                          />
                          <ArrowForwardIosIcon
                            fontSize="small"
                            sx={{ opacity: 0.5 }}
                          />
                        </ListItemButton>
                        {idx < chatMatches.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </ResultCard>

            {/* MESSAGES */}
            {msgSupported && (
              <ResultCard
                title="Messages"
                icon={<SubdirectoryArrowRightIcon fontSize="small" />}
                count={Object.values(msgHits).reduce((a, b) => a + b.length, 0)}
                loading={msgLoading}
              >
                {msgLoading ? (
                  <Box sx={{ p: 2, display: "grid", placeItems: "center" }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : Object.keys(msgHits).length === 0 ? (
                  <MutedHint text="No message results." />
                ) : (
                  Object.entries(msgHits)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([cidStr, hits]) => {
                      const cid = Number(cidStr);
                      const ch = channelsByIdRef.current.get(cid);
                      return (
                        <Paper
                          key={`mgrp-${cid}`}
                          variant="outlined"
                          sx={{ mb: 1, overflow: "hidden" }}
                        >
                          <Box
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              bgcolor: "action.hover",
                              borderBottom: 1,
                              borderColor: "divider",
                            }}
                          >
                            <ScopeAvatar channel={ch || undefined} />
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 700 }}
                            >
                              {ch?.name || `Channel #${cid}`}
                            </Typography>
                            <Chip size="small" label={hits.length} />
                          </Box>

                          <List dense disablePadding>
                            {hits
                              .sort((a, b) =>
                                a.createdAt < b.createdAt ? 1 : -1
                              )
                              .map((m, i) => (
                                <React.Fragment key={`hit-${m.id}`}>
                                  <ListItemButton
                                    onClick={() => handleOpenMessageHit(m)}
                                    sx={{ alignItems: "flex-start" }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="body2"
                                          sx={{ display: "flex", gap: 1 }}
                                        >
                                          <span style={{ fontWeight: 600 }}>
                                            {m.author?.profile?.name ||
                                              m.author?.username ||
                                              "User"}
                                          </span>
                                          <span style={{ opacity: 0.7 }}>
                                            •{" "}
                                            {dayjs(m.createdAt).format(
                                              "MMM D, HH:mm"
                                            )}
                                          </span>
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography
                                          variant="body2"
                                          color="text.primary"
                                          sx={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {highlight(
                                            m.snippet ||
                                              truncate(m.content, 140),
                                            query
                                          )}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                  {i < hits.length - 1 && (
                                    <Divider component="li" />
                                  )}
                                </React.Fragment>
                              ))}
                          </List>
                        </Paper>
                      );
                    })
                )}
              </ResultCard>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/* ====================== UI building blocks ====================== */

function ScopeCard({
  title,
  count,
  scope,
  children,
  collapsible,
  open,
  onToggle,
  defaultOpen,
  dense,
}: {
  title: string;
  count: number;
  scope?: ChannelScope;
  children: React.ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  defaultOpen?: boolean;
  dense?: boolean;
}) {
  const theme = useTheme();
  const color = scope
    ? getScopeColor(scope, theme)
    : theme.palette.text.primary;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderRadius: 2,
        borderColor: alpha(theme.palette.divider, 0.6),
      }}
    >
      <List
        dense={dense}
        subheader={
          <ListSubheader
            component="div"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.25,
              py: 1,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              backdropFilter: "blur(4px)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {scope && (
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    bgcolor: alpha(color, 0.16),
                    color,
                    fontSize: 14,
                  }}
                >
                  {getScopeIcon(scope)}
                </Avatar>
              )}
              <Typography variant="subtitle2" fontWeight={700}>
                {title}
              </Typography>
              <Chip size="small" label={count} />
            </Box>
            {collapsible && (
              <IconButton
                size="small"
                onClick={onToggle}
                aria-label="Toggle section"
              >
                {open ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </ListSubheader>
        }
        sx={{ pt: 0, pb: 0 }}
      >
        {collapsible ? (
          <Collapse
            in={open ?? defaultOpen ?? true}
            timeout="auto"
            unmountOnExit
          >
            {children}
          </Collapse>
        ) : (
          children
        )}
      </List>
    </Paper>
  );
}

function ResultCard({
  title,
  icon,
  count,
  loading,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  count: number;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
      <Box
        sx={{
          px: 1.25,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {icon}
          <Typography variant="subtitle2">{title}</Typography>
          {loading ? (
            <CircularProgress size={14} sx={{ ml: 1 }} />
          ) : (
            <Chip size="small" label={count} sx={{ ml: 0.5 }} />
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
}

function MutedHint({ text }: { text: string }) {
  return (
    <Box sx={{ p: 1.25 }}>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
}

function ScopeAvatar({ channel }: { channel?: Channel }) {
  const theme = useTheme();
  if (!channel) {
    return (
      <Avatar sx={{ width: 24, height: 24 }}>
        <ChatBubbleOutlineIcon fontSize="small" />
      </Avatar>
    );
  }
  const color = getScopeColor(channel.scope, theme);
  return (
    <Avatar
      sx={{
        width: 24,
        height: 24,
        bgcolor: alpha(color, 0.15),
        color,
        fontSize: 14,
      }}
    >
      {getScopeIcon(channel.scope)}
    </Avatar>
  );
}

function ChannelRow({
  channel,
  unread,
  selected,
  onOpen,
  onTogglePin,
  pinned,
}: {
  channel: Channel;
  unread: number;
  selected: boolean;
  onOpen: () => void;
  onTogglePin: () => void;
  pinned: boolean;
}) {
  const theme = useTheme();
  const color = getScopeColor(channel.scope, theme);

  return (
    <ListItemButton
      selected={selected}
      onClick={onOpen}
      sx={{
        alignItems: "center",
        "&.Mui-selected": {
          bgcolor: alpha(color, 0.08),
        },
        "&:hover": { bgcolor: alpha(color, 0.06) },
      }}
    >
      <ScopeAvatar channel={channel} />
      <ListItemText
        primary={channel.name}
        secondary={niceScopeLabel(channel.scope)}
        secondaryTypographyProps={{ sx: { opacity: 0.7 } }}
        sx={{ ml: 1 }}
      />

      <Badge
        color="primary"
        badgeContent={unread}
        max={99}
        invisible={unread === 0}
        sx={{ mr: 0.5 }}
      />

      <Tooltip title={pinned ? "Unpin" : "Pin"}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin();
          }}
          edge="end"
          aria-label={pinned ? "Unpin channel" : "Pin channel"}
          sx={{ ml: 0.5 }}
        >
          {pinned ? (
            <StarRoundedIcon fontSize="small" color="warning" />
          ) : (
            <StarBorderRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>

      <ArrowForwardIosIcon fontSize="small" sx={{ opacity: 0.4, ml: 0.5 }} />
    </ListItemButton>
  );
}

/* ---------- tiny chip helpers ---------- */

function FilterChip({
  label,
  selected,
  onClick,
  icon,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactElement; // ⬅️ narrow type
}) {
  return (
    <Chip
      size="small"
      variant={selected ? "filled" : "outlined"}
      color={selected ? "primary" : "default"}
      label={label}
      onClick={onClick}
      {...(icon ? { icon } : {})} // ⬅️ only pass when present
      sx={{ borderRadius: 2 }}
    />
  );
}

function ScopeFilterChip({
  scope,
  count,
  selected,
  onClick,
}: {
  scope: ChannelScope;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  const color = getScopeColor(scope, theme);
  return (
    <Chip
      size="small"
      variant={selected ? "filled" : "outlined"}
      sx={{
        borderRadius: 2,
        ...(selected
          ? { bgcolor: alpha(color, 0.16), color }
          : { borderColor: alpha(color, 0.4), color }),
      }}
      onClick={onClick}
      icon={getScopeIcon(scope)}
      label={`${niceScopeLabel(scope)} (${count})`}
    />
  );
}
